import { useMemo, useState } from 'react';
import dayjs from 'dayjs';

import { useEliseAvailability } from 'src/services/api/elise-ai';

const AVAILABILITY_WINDOW_DAYS = 30;

interface Options {
    open: boolean;
    eliseBuildingId: string | undefined;
    initialDate?: dayjs.Dayjs | null;
}

/**
 * Availability-fetch + available-date derivation for booking a tour through Elise.ai's
 * scheduling widget. Used inline inside CreateOrUpdateTourModal, once the selected property
 * turns out to be Elise-integrated.
 */
export function useEliseTourSlotSelection(options: Options) {
    const { open, eliseBuildingId, initialDate } = options;

    const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(null);

    const windowStart = useMemo(() => dayjs().toISOString(), []);
    const windowEnd = useMemo(
        () => dayjs().add(AVAILABILITY_WINDOW_DAYS, 'day').toISOString(),
        []
    );

    const {
        data: availability,
        isLoading: isAvailabilityLoading,
        isError: isAvailabilityError,
    } = useEliseAvailability(open ? eliseBuildingId : undefined, windowStart, windowEnd);

    // Elise's own timezone for this building -- everything below (day grouping, the date
    // picker, slot labels) is anchored to this rather than the viewer's browser timezone, so
    // the modal reads the same regardless of which timezone the agent happens to be sitting in.
    const eliseTimezone = availability?.calendarTimeZone;

    const availableSlots = useMemo(() => {
        if (!availability) {
            return [];
        }
        return Object.values(availability.availability).flatMap((a) => a.availableTourStartTimes);
    }, [availability]);

    // Days that have at least one available slot -- used to restrict which calendar days can
    // be picked, since Elise's availability is sparse (not every day has openings).
    const availableDates = useMemo(() => {
        const dates = new Set<string>();
        for (const slot of availableSlots) {
            dates.add(eliseTimezone ? dayjs(slot).tz(eliseTimezone).format('YYYY-MM-DD') : dayjs(slot).format('YYYY-MM-DD'));
        }
        return dates;
    }, [availableSlots, eliseTimezone]);

    // Until the agent picks a date, prefer initialDate if it has openings (e.g. a calendar day
    // they clicked, or the date of the tour being edited); otherwise default to the earliest
    // day with openings, so they aren't staring at an empty slot grid.
    const effectiveSelectedDate = useMemo(() => {
        if (selectedDate) {
            return selectedDate;
        }
        const initialDateKey = initialDate &&
            (eliseTimezone ? initialDate.tz(eliseTimezone).format('YYYY-MM-DD') : initialDate.format('YYYY-MM-DD'));
        if (initialDateKey && availableDates.has(initialDateKey)) {
            return eliseTimezone ? dayjs.tz(initialDateKey, eliseTimezone) : dayjs(initialDateKey);
        }
        if (availableDates.size === 0) {
            return null;
        }
        const earliest = [...availableDates].sort()[0];
        return eliseTimezone ? dayjs.tz(earliest, eliseTimezone) : dayjs(earliest);
    }, [availableDates, selectedDate, initialDate, eliseTimezone]);

    const slotsForSelectedDate = useMemo(() => {
        if (!effectiveSelectedDate) {
            return [];
        }
        const dateKey = effectiveSelectedDate.format('YYYY-MM-DD');
        return availableSlots.filter((slot) => {
            const slotDateKey = eliseTimezone ? dayjs(slot).tz(eliseTimezone).format('YYYY-MM-DD') : dayjs(slot).format('YYYY-MM-DD');
            return slotDateKey === dateKey;
        });
    }, [availableSlots, effectiveSelectedDate, eliseTimezone]);

    const isDateUnavailable = (date: dayjs.Dayjs) => !availableDates.has(
        eliseTimezone ? date.tz(eliseTimezone).format('YYYY-MM-DD') : date.format('YYYY-MM-DD')
    );

    return {
        selectedDate: effectiveSelectedDate,
        setSelectedDate,
        isDateUnavailable,
        slotsForSelectedDate,
        eliseTimezone,
        isAvailabilityLoading,
        isAvailabilityError,
        AVAILABILITY_WINDOW_DAYS,
    };
}
